/* eslint-disable react-hooks/exhaustive-deps */
import { Controller, FieldError, UseFormReturn } from "react-hook-form"
import { useTranslation } from "react-i18next"
import type { WorkDaysFormValues } from "./types"
import { TimeInput, type TimeInputProps } from "./TimeInput"
import { TimeSheetEntryTypeEnum, WeekDaysEnum } from "@oysterhr/schema"
import { Icon, IconButton, Select, Switch, Text, Tooltip } from "@oysterhr/ui"
import { useEffect, useState } from "react"
import {
  getHoursDuration,
  getMillisRange,
  presentHoursHigherThan,
  rangesOverlap,
  breakIntervalRangeValid,
  adjustToNextDay,
  Range,
} from "./utils"
import { formatDurationToHoursMinutes } from "@oysterhr/common"

export interface WorkScheduleFormProps extends Pick<TimeInputProps, "isClearable"> {
  day: WeekDaysEnum
  index: number
  errors?: {
    from?: FieldError
    to?: FieldError
    break?: FieldError
    breakIntervalFrom?: FieldError
    breakIntervalTo?: FieldError
    entryType?: FieldError
  }
  onWorkScheduleChange?: (workDays: WorkDaysFormValues["workDays"]) => void
  form: UseFormReturn<WorkDaysFormValues>
  availableEntryTypes: TimeSheetEntryTypeEnum[]
}

export function WorkScheduleFormRow({
  day,
  errors,
  index,
  isClearable = true,
  onWorkScheduleChange,
  form,
  availableEntryTypes,
}: WorkScheduleFormProps) {
  const { t } = useTranslation("WorkScheduleForm")

  const { getValues, clearErrors, control, setValue, trigger } = form

  const [showBreak, setShowBreak] = useState(
    getValues(`workDays.${day}.${index}.breakIntervalFrom`) !== undefined,
  )

  useEffect(() => {
    const breakIntervalFrom = getValues(`workDays.${day}.${index}.breakIntervalFrom`)
    if (showBreak && breakIntervalFrom === undefined) {
      setShowBreak(false)
    } else if (!showBreak && breakIntervalFrom !== undefined) {
      setShowBreak(true)
    }
  })

  const daysHourRanges = (index: number, from?: string, to?: string): Range[] => {
    const entries =
      getValues(`workDays.${day}`)
        ?.filter(entry => entry.from && entry.to)
        .map(entry => getMillisRange(entry)) || []

    const start = from || getValues(`workDays.${day}.${index}.from`)
    const end = to || getValues(`workDays.${day}.${index}.to`)

    if (start !== "" && end !== "") {
      entries[index] = getMillisRange({ from: start, to: adjustToNextDay(end) })
    }

    return entries
  }

  const presentHours = getHoursDuration(
    getValues(`workDays.${day}.${index}.from`),
    getValues(`workDays.${day}.${index}.to`),
  )
  const presentHoursText = formatDurationToHoursMinutes(presentHours)

  const breakHours = getHoursDuration(
    getValues(`workDays.${day}.${index}.breakIntervalFrom`),
    getValues(`workDays.${day}.${index}.breakIntervalTo`),
  )
  const breakHoursText = formatDurationToHoursMinutes(breakHours)

  const timeErrors =
    errors &&
    (errors.from ||
      errors.to ||
      errors.entryType ||
      errors.breakIntervalFrom ||
      errors.breakIntervalTo)

  return (
    <tr className="w-64 border-y py-2 first:border-0 last:border-0">
      <td className="w-64 py-2 align-top">
        <div className="flex items-start gap-2">
          <div className="flex min-h-8 items-center justify-between space-x-6"></div>
          <div className="flex items-center gap-2">
            <div className="w-4">
              {timeErrors && (
                <Tooltip value={timeErrors.message}>
                  <Icon name="WarningCircleFill" className="text-error-base" />
                </Tooltip>
              )}
            </div>
            <div className="h-8 w-24">
              <Controller
                control={control}
                name={`workDays.${day}.${index}.from`}
                rules={{
                  validate: value => {
                    if (!value || (value && getValues(`workDays.${day}.${index}.to`) === null)) {
                      return t("validations.workScheduleRequired") as string
                    }
                    if (
                      getValues(`workDays.${day}.${index}.to`) <= value &&
                      !getValues(`workDays.${day}.${index}.to`).includes("T00:00")
                    ) {
                      return t("validations.clockOutBeforeClockIn") as string
                    }
                    if (presentHoursHigherThan(getValues(`workDays.${day}`) || [], 24)) {
                      return t("validations.totalHoursExceeded") as string
                    }
                    if (
                      rangesOverlap(
                        daysHourRanges(index, value, getValues(`workDays.${day}.${index}.to`)),
                      )
                    ) {
                      return t("validations.entryOverlaps") as string
                    }
                    if (
                      !breakIntervalRangeValid(
                        value,
                        getValues(`workDays.${day}.${index}.to`),
                        getValues(`workDays.${day}.${index}.breakIntervalFrom`),
                        getValues(`workDays.${day}.${index}.breakIntervalTo`),
                      )
                    ) {
                      return t("validations.breakIntervalOutside") as string
                    }

                    return true
                  },
                }}
                render={({ field: { value, onChange, name } }) => (
                  <TimeInput
                    name={name}
                    value={value}
                    onChange={value => {
                      onChange(value)
                      clearErrors(`workDays.${day}`)
                      trigger(`workDays.${day}.${index}`)
                      onWorkScheduleChange?.(getValues("workDays"))
                    }}
                    error={errors?.from || errors?.to}
                  />
                )}
              />
            </div>

            <Icon name="ArrowRight" />

            <div className="h-8 w-24">
              <Controller
                control={control}
                name={`workDays.${day}.${index}.to`}
                rules={{
                  validate: value => {
                    if (!value || (value && getValues(`workDays.${day}.${index}.from`) === null)) {
                      return t("validations.workScheduleRequired") as string
                    }
                    if (
                      getValues(`workDays.${day}.${index}.from`) >= value &&
                      !value.includes("T00:00")
                    ) {
                      return t("validations.clockOutBeforeClockIn") as string
                    }
                    if (presentHoursHigherThan(getValues(`workDays.${day}`) || [], 24)) {
                      return t("validations.totalHoursExceeded") as string
                    }
                    if (
                      rangesOverlap(
                        daysHourRanges(index, getValues(`workDays.${day}.${index}.from`), value),
                      )
                    ) {
                      return t("validations.entryOverlaps") as string
                    }
                    if (
                      !breakIntervalRangeValid(
                        getValues(`workDays.${day}.${index}.from`),
                        value,
                        getValues(`workDays.${day}.${index}.breakIntervalFrom`),
                        getValues(`workDays.${day}.${index}.breakIntervalTo`),
                      )
                    ) {
                      return t("validations.breakIntervalOutside") as string
                    }
                    return true
                  },
                }}
                render={({ field: { value, onChange, name } }) => (
                  <TimeInput
                    name={name}
                    value={value}
                    onChange={value => {
                      onChange(value)
                      clearErrors(`workDays.${day}`)
                      trigger(`workDays.${day}.${index}`)
                      onWorkScheduleChange?.(getValues("workDays"))
                    }}
                    error={errors?.from || errors?.to}
                  />
                )}
              />
            </div>
          </div>
        </div>
      </td>
      <td className="min-w-96 py-2 align-top">
        <div className="flex items-start gap-2">
          <div className="min-h-8 w-48">
            <Controller
              control={control}
              name={`workDays.${day}.${index}.entryType`}
              rules={{
                validate: value => {
                  if (!value) {
                    return t("validations.typeRequired") as string
                  }
                  return true
                },
              }}
              render={({ field: { value, onChange, name } }) => (
                <Select
                  size="sm"
                  name={name}
                  value={value}
                  placeholder={t("table.typePlaceholder")}
                  onChange={value => {
                    onChange(value)
                    trigger(`workDays.${day}.${index}.entryType`)
                    onWorkScheduleChange?.(getValues("workDays"))
                  }}
                  UNSAFE_selectedValueDisplay={selectedValue => (
                    <div className="flex w-full overflow-auto whitespace-nowrap">
                      {t(`table.types.${selectedValue}`)}
                    </div>
                  )}
                  error={errors?.entryType?.message}
                >
                  {availableEntryTypes.map((entryType: TimeSheetEntryTypeEnum) => (
                    <Select.Option key={entryType} value={entryType}>
                      <div>{t(`table.types.${entryType}`)}</div>
                      <div className="font-regular text-sm text-gray-400">
                        {t(`table.typesDescriptions.${entryType}`)}
                      </div>
                    </Select.Option>
                  ))}
                </Select>
              )}
            />
          </div>
          <div className="flex min-h-8 items-center gap-2">
            <Text className="font-medium">{t("table.break")}</Text>
            <Switch
              onChange={() => {
                if (showBreak) {
                  setValue(`workDays.${day}.${index}.breakIntervalFrom`, undefined)
                  setValue(`workDays.${day}.${index}.breakIntervalTo`, undefined)
                  trigger(`workDays.${day}.${index}`)
                } else {
                  setValue(`workDays.${day}.${index}.breakIntervalFrom`, "")
                  setValue(`workDays.${day}.${index}.breakIntervalTo`, "")
                }
                setShowBreak(!showBreak)
              }}
              checked={showBreak}
              data-testid={`breakSwitch-${day}-${index}`}
            />
            {showBreak && (
              <div className="flex items-center gap-2">
                <div className="min-h-8 w-24">
                  <Controller
                    control={control}
                    name={`workDays.${day}.${index}.breakIntervalFrom`}
                    rules={{
                      validate: value => {
                        const breakIntervalTo = getValues(
                          `workDays.${day}.${index}.breakIntervalTo`,
                        )
                        if (!value || (value && breakIntervalTo === null)) {
                          return t("validations.workScheduleRequired") as string
                        }
                        if (
                          breakIntervalTo &&
                          breakIntervalTo <= value &&
                          !breakIntervalTo.includes("T00:00")
                        ) {
                          return t("validations.fromGreaterThanTo") as string
                        }
                        if (
                          !breakIntervalRangeValid(
                            getValues(`workDays.${day}.${index}.from`),
                            getValues(`workDays.${day}.${index}.to`),
                            value,
                            breakIntervalTo,
                          )
                        ) {
                          return t("validations.breakIntervalOutside") as string
                        }
                        return true
                      },
                    }}
                    render={({ field: { value, onChange, name } }) => (
                      <TimeInput
                        name={name}
                        value={value || ""}
                        onChange={value => {
                          onChange(value)
                          trigger(`workDays.${day}.${index}`)
                          onWorkScheduleChange?.(getValues("workDays"))
                        }}
                        error={errors?.breakIntervalFrom}
                      />
                    )}
                  />
                </div>

                <Icon name="ArrowRight" />

                <div className="min-h-8 w-24">
                  <Controller
                    control={control}
                    name={`workDays.${day}.${index}.breakIntervalTo`}
                    rules={{
                      validate: value => {
                        const breakIntervalFrom = getValues(
                          `workDays.${day}.${index}.breakIntervalFrom`,
                        )
                        if (!value || (value && breakIntervalFrom === null)) {
                          return t("validations.workScheduleRequired") as string
                        }
                        if (
                          breakIntervalFrom &&
                          breakIntervalFrom >= value &&
                          !value.includes("T00:00")
                        ) {
                          return t("validations.fromGreaterThanTo") as string
                        }
                        if (
                          !breakIntervalRangeValid(
                            getValues(`workDays.${day}.${index}.from`),
                            getValues(`workDays.${day}.${index}.to`),
                            getValues(`workDays.${day}.${index}.breakIntervalFrom`),
                            value,
                          )
                        ) {
                          return t("validations.breakIntervalOutside") as string
                        }
                        return true
                      },
                    }}
                    render={({ field: { value, onChange, name } }) => (
                      <TimeInput
                        name={name}
                        value={value || ""}
                        onChange={value => {
                          onChange(value)
                          trigger(`workDays.${day}.${index}`)
                          onWorkScheduleChange?.(getValues("workDays"))
                        }}
                        error={errors?.breakIntervalTo}
                      />
                    )}
                  />
                </div>
              </div>
            )}
            {isClearable && (
              <IconButton
                iconName="Trash"
                size="sm"
                variation="danger--borderless"
                aria-label="Delete"
                data-testid={`delete-${day}-${index}`}
                onClick={() => {
                  const currentHours = getValues(`workDays.${day}`) || []
                  setValue(`workDays.${day}`, [
                    ...currentHours.slice(0, index).concat(currentHours.slice(index + 1)),
                  ])
                  trigger()
                }}
              />
            )}
          </div>
        </div>
      </td>
      <td
        className="sticky right-0 min-w-32 bg-white py-2 text-right align-top"
        data-testid={`totalHours-${day}-${index}`}
      >
        {presentHours && <span className="text-sm">+{presentHoursText}</span>}

        {breakHours && <span className="text-sm text-gray-300">, -{breakHoursText}</span>}
      </td>
    </tr>
  )
}
